import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { GripVertical, Plus, Search } from 'lucide-react'

const DEVICE_TYPE_ICONS: Record<string, string> = {
  workstation: '\u{1F4BB}',
  'domain-controller': '\u{1F3DB}\uFE0F',
  'file-server': '\u{1F4C1}',
  'mail-server': '\u{1F4E7}',
  'database-server': '\u{1F5C4}\uFE0F',
  'proxy-server': '\u{1F500}',
  firewall: '\u{1F6E1}\uFE0F',
  'network-switch': '\u{1F50C}',
  router: '\u{1F4E1}',
  server: '\u{1F5A5}\uFE0F',
  laptop: '\u{1F4BB}',
  mobile: '\u{1F4F1}',
  unknown: '\u{2753}',
}

interface OrphanItemProps {
  id: string
  hostname: string
  ipAddress: string
  deviceType: string
  onDragStart: (endpointId: string) => void
  onAddToMap: (endpointId: string) => void
  t: TFunction<'cases'>
}

export function OrphanItem({
  id,
  hostname,
  ipAddress,
  deviceType,
  onDragStart,
  onAddToMap,
  t,
}: OrphanItemProps): ReactElement {
  const icon = DEVICE_TYPE_ICONS[deviceType] ?? DEVICE_TYPE_ICONS.unknown
  const handleDragStart = (e: React.DragEvent): void => {
    e.dataTransfer.setData('text/plain', id)
    e.dataTransfer.effectAllowed = 'move'
    onDragStart(id)
  }
  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className="group flex cursor-grab items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-100 active:cursor-grabbing dark:hover:bg-slate-700"
    >
      <GripVertical className="h-3 w-3 flex-shrink-0 text-slate-300 group-hover:text-slate-400 dark:text-slate-600 dark:group-hover:text-slate-500" />
      <span className="flex-shrink-0 text-sm">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
          {hostname}
        </div>
        <div className="font-mono text-xs text-slate-500 dark:text-slate-400">{ipAddress}</div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onAddToMap(id)
        }}
        className="rounded p-1 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-fuchsia-100 hover:text-fuchsia-600 dark:hover:bg-fuchsia-900/30 dark:hover:text-fuchsia-400"
        title={t('infrastructure.canvas.addToMap')}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  )
}

interface EmptyShellProps {
  t: TFunction<'cases'>
}

export function EmptyOrphanPanel({ t }: EmptyShellProps): ReactElement {
  return (
    <div className="flex w-64 flex-col border-l border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50">
      <div className="border-b border-slate-200 p-3 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t('infrastructure.canvas.orphanEndpoints')}
        </h3>
      </div>
      <div className="flex flex-1 items-center justify-center p-4">
        <p className="text-center text-sm text-slate-500 dark:text-slate-400">
          {t('infrastructure.canvas.allEndpointsLinked')}
          <br />
          {t('infrastructure.canvas.greatWork')}
        </p>
      </div>
    </div>
  )
}

interface PanelHeaderProps {
  totalCount: number
  searchQuery: string
  onSearchChange: (value: string) => void
  t: TFunction<'cases'>
}

export function PanelHeader({
  totalCount,
  searchQuery,
  onSearchChange,
  t,
}: PanelHeaderProps): ReactElement {
  return (
    <div className="border-b border-slate-200 p-3 dark:border-slate-700">
      <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {t('infrastructure.canvas.orphanEndpoints')}
        <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
          ({totalCount})
        </span>
      </h3>
      <div className="relative">
        <Search className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          placeholder={t('infrastructure.canvas.searchOrphans')}
          className="w-full rounded-md border border-slate-200 bg-white py-1.5 pr-3 pl-8 text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-fuchsia-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />
      </div>
    </div>
  )
}

interface InstructionsProps {
  t: TFunction<'cases'>
}

export function PanelInstructions({ t }: InstructionsProps): ReactElement {
  return (
    <div className="border-b border-slate-200 bg-slate-100 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50">
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        {t('infrastructure.canvas.dragEndpointsInstruction')}{' '}
        <Plus className="inline-block h-3 w-3" />{' '}
        {t('infrastructure.canvas.dragEndpointsInstructionSuffix')}
      </p>
    </div>
  )
}

interface FooterProps {
  filteredCount: number
  totalCount: number
  t: TFunction<'cases'>
}

export function PanelFooter({ filteredCount, totalCount, t }: FooterProps): ReactElement | null {
  if (filteredCount === totalCount) return null
  return (
    <div className="border-t border-slate-200 p-2 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
      {t('infrastructure.canvas.showingOfTotal', { filtered: filteredCount, total: totalCount })}
    </div>
  )
}
